/**
 * The only browser-file plumbing in `src/importExport`: turning a `Blob` into a download and
 * reading a picked `File` back. DOM access is deliberately confined to this module so the rest of
 * the GLB pipeline stays testable under vitest's node environment.
 */

import { AppError } from '../shared';

/** Trigger a browser download for `blob` under `filename`. */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new AppError(
      'DOWNLOAD_UNAVAILABLE',
      'Downloads are only available in the browser.',
      { filename },
    );
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick: Safari cancels the download if the URL dies too early.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function readFile<T extends string | ArrayBuffer>(
  file: File | Blob,
  mode: 'text' | 'arrayBuffer',
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (typeof FileReader === 'undefined') {
      reject(new AppError('FILE_READ_FAILED', 'File reading is only available in the browser.'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => {
      reject(
        new AppError(
          'FILE_READ_FAILED',
          `The file could not be read: ${reader.error?.message ?? 'unknown error'}.`,
          reader.error,
        ),
      );
    };
    reader.onload = () => resolve(reader.result as T);
    if (mode === 'text') reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
  });
}

/** Read a picked file as UTF-8 text (animation-library JSON). */
export function readFileAsText(file: File | Blob): Promise<string> {
  return readFile<string>(file, 'text');
}

/** Read a picked file as an `ArrayBuffer` (GLB). */
export function readFileAsArrayBuffer(file: File | Blob): Promise<ArrayBuffer> {
  return readFile<ArrayBuffer>(file, 'arrayBuffer');
}
